import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { dateAtFraction, playbackDurationMs, progressFraction } from '../../utils/timeline';
import type { TimelineRange } from '../atoms/Timeline';

/** Selectable play-through speeds (multipliers); 1 is the default. Higher = faster (shorter sweep). */
export const PLAYBACK_SPEEDS = [0.25, 0.5, 1, 1.5, 2] as const;

export interface TimelinePlayback {
  /**
   * The scrubbed instant the whole care-plan view reflects, or null when "live" (not scrubbing —
   * everything shows its current state). Charts reveal observations at or before this time; the board
   * hides items added after it.
   */
  currentTime: Date | null;
  /** Jump to a specific instant (manual scrub of the playhead); pauses any play-through. */
  seekToTime: (time: Date) => void;
  /** Return to live — clears `currentTime` so the view reverts to its present state. */
  stop: () => void;
  /**
   * The active drag-zoom window (the timeline's axis scope), or null when not zoomed. Published by the
   * timeline so the detail-overlay charts mirror the same x-axis; a selected range persists across
   * opening/closing a topic overlay.
   */
  zoomRange: TimelineRange | null;
  setZoomRange: (range: TimelineRange | null) => void;
  /**
   * A pending request to zoom the timeline to a range, published by a chart's zoom-to-fit control. The
   * timeline owner consumes it (pushing onto its zoom stack) so a chart-driven zoom routes through the
   * same path as a drag-zoom and stays undoable. The `nonce` lets an identical range re-fire. Null when
   * there's no outstanding request.
   */
  zoomRequest: { range: TimelineRange; nonce: number } | null;
  /**
   * Request the timeline zoom to a range (from a chart's zoom-to-fit button). Absent from the no-op
   * default, so a component can key off its presence to know a zoomable timeline is actually mounted.
   */
  requestZoom?: (range: TimelineRange) => void;
  /** True while the play-through animation is advancing `currentTime` across `playbackRange`. */
  isPlaying: boolean;
  /**
   * The window the play button sweeps (the displayed/zoomed range), published by the timeline. Null when
   * there's too little dated data to play through, so the control hides.
   */
  playbackRange: TimelineRange | null;
  setPlaybackRange: (range: TimelineRange | null) => void;
  /** Start (or resume) the play-through across `playbackRange`. No-op without a range. */
  play: () => void;
  /** Freeze the play-through at the current instant (the view stays scrubbed). */
  pause: () => void;
  /** Play-through speed multiplier (one of {@link PLAYBACK_SPEEDS}); higher = faster. */
  speed: number;
  setSpeed: (speed: number) => void;
}

const defaultValue: TimelinePlayback = {
  currentTime: null,
  seekToTime: () => undefined,
  stop: () => undefined,
  zoomRange: null,
  setZoomRange: () => undefined,
  zoomRequest: null,
  isPlaying: false,
  playbackRange: null,
  setPlaybackRange: () => undefined,
  play: () => undefined,
  pause: () => undefined,
  speed: 1,
  setSpeed: () => undefined,
};

export const TimelinePlaybackContext = createContext<TimelinePlayback>(defaultValue);

/**
 * State for the care-plan timeline scrubber + playback. Safe to consume without a provider (returns a
 * live, no-op default) so shared components — the board, measurement charts — can read `currentTime`
 * unconditionally whether or not the feature is mounted; it is only provided when the `timelineScrub`
 * flag is on, so `currentTime` stays null (and every reveal is inert) otherwise.
 */
export function useTimelinePlayback(): TimelinePlayback {
  return useContext(TimelinePlaybackContext);
}

// The playhead emits an update on this cadence (not every animation frame), so a play-through of any
// length stays smooth (~10 updates/sec) without storming the board with re-renders.
const PLAYBACK_STEP_MS = 100;

/**
 * Owns the scrubber's `currentTime`/`isPlaying` and the requestAnimationFrame play-through loop, and
 * builds the value to provide via {@link TimelinePlaybackContext}. The loop advances `currentTime`
 * across `playbackRange` at a real rate set by {@link playbackDurationMs} scaled by the current `speed`
 * multiplier, emitting on a fixed time cadence so the reveal stays smooth without storming the board.
 */
export function useTimelinePlaybackState(): TimelinePlayback {
  const [currentTime, setCurrentTime] = useState<Date | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackRange, setPlaybackRange] = useState<TimelineRange | null>(null);
  const [zoomRange, setZoomRange] = useState<TimelineRange | null>(null);
  const [zoomRequest, setZoomRequest] = useState<{ range: TimelineRange; nonce: number } | null>(null);
  const [speed, setSpeed] = useState(1);

  // Bump a nonce alongside the range so requesting the same extent twice (e.g. re-clicking a chart's
  // zoom-to-fit after a manual zoom) still notifies the timeline consumer.
  const requestZoom = useCallback(
    (range: TimelineRange) => setZoomRequest((prev) => ({ range, nonce: (prev?.nonce ?? 0) + 1 })),
    [],
  );

  const rangeRef = useRef<TimelineRange | null>(null);
  rangeRef.current = playbackRange;
  const speedRef = useRef(1);
  speedRef.current = speed;
  const currentTimeRef = useRef<Date | null>(null);
  currentTimeRef.current = currentTime;
  const rafRef = useRef<number | null>(null);
  const playingRef = useRef(false);

  const cancelRaf = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const pause = useCallback(() => {
    playingRef.current = false;
    cancelRaf();
    setIsPlaying(false);
  }, [cancelRaf]);

  const stop = useCallback(() => {
    pause();
    setCurrentTime(null);
  }, [pause]);

  const play = useCallback(() => {
    const range = rangeRef.current;
    if (!range) return;

    cancelRaf();
    playingRef.current = true;
    setIsPlaying(true);

    // 1× wall-clock duration; each frame advances a share of it scaled by the *current* speed, so
    // changing speed mid-play re-paces the sweep immediately (rather than only on the next play).
    const baseDurationMs = playbackDurationMs(range);
    // Resume from the paused instant when it sits inside the window; otherwise start from the beginning.
    const cur = currentTimeRef.current;
    let fraction = cur && cur >= range.start && cur < range.end ? progressFraction(cur, range) : 0;
    setCurrentTime(dateAtFraction(fraction, range));
    let lastNow = performance.now();
    let lastEmit = lastNow;

    const tick = (now: number): void => {
      if (!playingRef.current) return;
      const dt = now - lastNow;
      lastNow = now;
      fraction = Math.min(1, fraction + (dt / baseDurationMs) * speedRef.current);
      // Emit on a fixed time cadence, not every animation frame, to bound board re-renders.
      if (fraction >= 1 || now - lastEmit >= PLAYBACK_STEP_MS) {
        lastEmit = now;
        setCurrentTime(dateAtFraction(fraction, range));
      }

      if (fraction >= 1) {
        // Play-through done — return to live so the board shows current data again. The playhead stays
        // at the right edge (it falls back to the range end when live), so there's no jump.
        playingRef.current = false;
        rafRef.current = null;
        setCurrentTime(null);
        setIsPlaying(false);
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
  }, [cancelRaf]);

  const seekToTime = useCallback(
    (time: Date) => {
      pause();
      setCurrentTime(time);
    },
    [pause],
  );

  // Stop the loop if the range disappears (e.g. patient switch) or on unmount.
  useEffect(() => {
    if (!playbackRange) {
      pause();
      setCurrentTime(null);
    }
  }, [playbackRange, pause]);

  useEffect(() => cancelRaf, [cancelRaf]);

  return useMemo<TimelinePlayback>(
    () => ({
      currentTime,
      seekToTime,
      stop,
      zoomRange,
      setZoomRange,
      zoomRequest,
      requestZoom,
      isPlaying,
      playbackRange,
      setPlaybackRange,
      play,
      pause,
      speed,
      setSpeed,
    }),
    [currentTime, seekToTime, stop, zoomRange, zoomRequest, requestZoom, isPlaying, playbackRange, play, pause, speed],
  );
}
